// React imports
import { useState, useContext } from "react";
import { Link as RouterLink } from "@tanstack/react-router";
// State
import { useDraftOrderStaff } from "../../state/draftOrderStaffState";
// Context
import { AlertNotificationContext } from "../../contexts/AlertNotificationContext";
// Hooks
import { useCheckAPI } from "../../hooks/useCheckAPI";
// Components
import { DeleteStaffDialog } from "./DeleteStaffDialog";
import { OrderItems } from "../../components/OrderItems";
import { ViewStaffItemsDialog } from "./ViewStaffItemsDialog";
import { OrderReasonSelect } from "../../components/OrderReasonSelect";
// MUI
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Link from "@mui/material/Link";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import InfoIcon from "@mui/icons-material/Info";

import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";

export function SelectedStaffTableRow({ person, order, isMobile = false }) {
  const [items, setItems] = useState({ selected: [], core: [], accessory: [] });
  const [reasons, setReasons] = useState([]);
  const { setAlertNotification } = useContext(AlertNotificationContext);

  // Get Order Staff Lines
  const { isPending, data, isSuccess, isError, error } = useDraftOrderStaff(
    order.no,
    person.staffLineNo,
    true,
  );

  // Success Function
  const successFunc = () => {
    const items = data.data.data.data.data.lines;
    if (!items) return;
    const reasons = data.data.data.data.data.reasons;
    const coreItems = items.filter((item) => item.type === "Core");
    const accessoryItems = items.filter((item) => item.type === "Accessory");
    const selectedItems = items.filter((item) => item.itemNo);
    setItems({
      selected: selectedItems,
      core: coreItems,
      accessory: accessoryItems,
    });
    setReasons(reasons);
  };

  // Error Function
  const errorFunc = () =>
    setAlertNotification({
      message: `Error: ${data.data.status}: ${data.data.statusText}`,
      severity: "error",
    });

  // Hook to check the BC API for errors
  useCheckAPI(isSuccess, data, data?.data?.status, successFunc, errorFunc);

  const StaffDetails = ({ person }) => {
    const [anchorEl, setAnchorEl] = useState(null);

    const handleClick = (event) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const staffError =
      person.blocked === "Yes" || person.contractBlocked === "Yes";

    const open = Boolean(anchorEl);
    const id = open ? "staff-details" : undefined;
    return (
      <div>
        <IconButton
          aria-describedby={id}
          aria-label="Details"
          color={staffError ? "error" : "info"}
          onClick={handleClick}
        >
          <InfoIcon />
        </IconButton>
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
        >
          <Box sx={{ p: 2 }}>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                justifyContent: "flex-start",
                alignItems: "center",
              }}
            >
              <Typography sx={{ p: 1 }}>Status:</Typography>
              {person.blocked === "Yes" ? (
                <Chip
                  label="Blocked"
                  variant="outlined"
                  color="error"
                  size="small"
                />
              ) : (
                <Chip
                  label="Active"
                  variant="outlined"
                  color="success"
                  size="small"
                />
              )}
            </Stack>
            <Typography sx={{ p: 1 }}>Wardrobe: {person.uniform}</Typography>
            <Typography sx={{ p: 1 }}>Branch: {person.branch}</Typography>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                justifyContent: "flex-start",
                alignItems: "center",
              }}
            >
              <Typography sx={{ p: 1 }}>
                Contract: {person.contractName}
              </Typography>
              {person.contractBlocked === "Yes" ? (
                <Chip
                  label="Blocked"
                  variant="outlined"
                  color="error"
                  size="small"
                />
              ) : (
                <Chip
                  label="Active"
                  variant="outlined"
                  color="success"
                  size="small"
                />
              )}
            </Stack>
            {person.staffpoNo && (
              <Typography sx={{ p: 1 }}>PO: {person.staffpoNo}</Typography>
            )}
          </Box>
        </Popover>
      </div>
    );
  };

  return (
    <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
      {isMobile ? (
        <TableCell align="center">
          <Stack
            direction="row"
            spacing={1}
            sx={{
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <OrderItems
              person={person}
              order={order}
              items={items}
              loading={isPending}
              itemsCount={person.numberOfLines}
              orderType="draft"
            />
            <ViewStaffItemsDialog
              order={order}
              person={person}
              itemsCount={person.numberOfLines}
              items={items.selected}
              orderType="draft"
            />
          </Stack>
        </TableCell>
      ) : (
        <>
          <TableCell align="center">
            <OrderItems
              person={person}
              order={order}
              items={items}
              loading={isPending}
              itemsCount={person.numberOfLines}
              orderType="draft"
            />
          </TableCell>
          <TableCell align="center">
            <ViewStaffItemsDialog
              order={order}
              person={person}
              itemsCount={person.numberOfLines}
              items={items.selected}
              orderType="draft"
            />
          </TableCell>
        </>
      )}
      <TableCell component="th" scope="row">
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: "center",
          }}
        >
          <Link
            color="inherit"
            component={RouterLink}
            to={`/staff/${person.no}`}
          >
            {person.name} ({person.yourId})
          </Link>
          {person.blocked === "Yes" && (
            <Chip
              label="Blocked"
              variant="outlined"
              color="error"
              size="small"
            />
          )}
          <StaffDetails person={person} />
        </Stack>
      </TableCell>

      <TableCell>
        {reasons.length > 0 && (
          <OrderReasonSelect
            reasons={reasons}
            reasonCode={person.reasonCode}
            order={order.no}
            person={person.staffLineNo}
            poNo={person.staffpoNo}
            size="small"
          />
        )}
      </TableCell>
      {/* {!isMobile && (
        <TableCell>
          <StaffDetails person={person} />
        </TableCell>
      )} */}
      <TableCell>
        <DeleteStaffDialog
          staffLineNo={person.staffLineNo}
          staffName={person.name}
          orderNo={order.no}
        />
      </TableCell>
    </TableRow>
  );
}
